import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, ChevronRight, ChevronLeft, Search, Target, TrendingUp, 
  Rocket, MessageSquare, Printer,
  BarChart3, ArrowRight, CheckCircle2, Mail, User
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import AssessmentResults from './AssessmentResults';
import { apiUrl } from '../utils/config';
import logger from '../utils/logger';

const FiveDAssessment = ({ isOpen, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('info'); // 'info' | 'questions' | 'results'
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const reportRef = useRef(null);

  // 5D Model dimensions with colors and icons
  const dimensions = {
    discover: { 
      name: t('assessment.dimensions.discover', 'Discover'), 
      icon: Search, 
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      description: t('assessment.dimensions.discoverDesc', 'Self-awareness, understanding strengths and growth areas')
    },
    define: { 
      name: t('assessment.dimensions.define', 'Define'), 
      icon: Target, 
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      description: t('assessment.dimensions.defineDesc', 'Goal clarity, vision setting, and strategic planning')
    },
    develop: { 
      name: t('assessment.dimensions.develop', 'Develop'), 
      icon: TrendingUp, 
      color: 'emerald',
      gradient: 'from-emerald-500 to-emerald-600',
      description: t('assessment.dimensions.developDesc', 'Skill building, learning, and continuous improvement')
    },
    drive: { 
      name: t('assessment.dimensions.drive', 'Drive'), 
      icon: Rocket, 
      color: 'orange',
      gradient: 'from-orange-500 to-orange-600',
      description: t('assessment.dimensions.driveDesc', 'Taking action, motivation, and maintaining momentum')
    },
    debrief: { 
      name: t('assessment.dimensions.debrief', 'Debrief'), 
      icon: MessageSquare, 
      color: 'rose',
      gradient: 'from-rose-500 to-rose-600',
      description: t('assessment.dimensions.debriefDesc', 'Reflection, feedback integration, and learning from experience')
    }
  };

  // 10 questions - 2 per dimension
  const questions = [
    // Discover (2 questions)
    {
      id: 1,
      dimension: 'discover',
      text: t('assessment.q1.text', 'I have a clear understanding of my personal strengths and weaknesses.'),
      subtext: t('assessment.q1.subtext', 'Self-awareness is the foundation of personal growth')
    },
    {
      id: 2,
      dimension: 'discover',
      text: t('assessment.q2.text', 'I regularly seek and am open to feedback from others about my performance.'),
      subtext: t('assessment.q2.subtext', 'External perspectives help complete our self-understanding')
    },
    // Define (2 questions)
    {
      id: 3,
      dimension: 'define',
      text: t('assessment.q3.text', 'I have clearly defined goals for where I want to be in 1-3 years.'),
      subtext: t('assessment.q3.subtext', 'Clear goals provide direction and motivation')
    },
    {
      id: 4,
      dimension: 'define',
      text: t('assessment.q4.text', 'I can articulate my core values and they guide my major decisions.'),
      subtext: t('assessment.q4.subtext', 'Values alignment creates authentic success')
    },
    // Develop (2 questions)
    {
      id: 5,
      dimension: 'develop',
      text: t('assessment.q5.text', 'I actively invest time in learning new skills relevant to my goals.'),
      subtext: t('assessment.q5.subtext', 'Continuous learning is key to staying competitive')
    },
    {
      id: 6,
      dimension: 'develop',
      text: t('assessment.q6.text', 'I have a structured approach to developing my capabilities.'),
      subtext: t('assessment.q6.subtext', 'Structured development accelerates growth')
    },
    // Drive (2 questions)
    {
      id: 7,
      dimension: 'drive',
      text: t('assessment.q7.text', 'I consistently take action towards my goals, even when motivation is low.'),
      subtext: t('assessment.q7.subtext', 'Discipline bridges the gap between goals and achievement')
    },
    {
      id: 8,
      dimension: 'drive',
      text: t('assessment.q8.text', 'I effectively manage my time and energy to maintain high performance.'),
      subtext: t('assessment.q8.subtext', 'Energy management sustains long-term success')
    },
    // Debrief (2 questions)
    {
      id: 9,
      dimension: 'debrief',
      text: t('assessment.q9.text', 'I regularly reflect on my experiences to extract lessons and insights.'),
      subtext: t('assessment.q9.subtext', 'Reflection transforms experience into wisdom')
    },
    {
      id: 10,
      dimension: 'debrief',
      text: t('assessment.q10.text', 'I adapt my approach based on what I learn from successes and failures.'),
      subtext: t('assessment.q10.subtext', 'Adaptability enables continuous improvement')
    }
  ];

  // Answer options (1-5 scale)
  const answerOptions = [
    { value: 1, label: t('assessment.scale.1', 'Strongly Disagree'), color: 'bg-red-500' },
    { value: 2, label: t('assessment.scale.2', 'Disagree'), color: 'bg-orange-500' },
    { value: 3, label: t('assessment.scale.3', 'Neutral'), color: 'bg-yellow-500' },
    { value: 4, label: t('assessment.scale.4', 'Agree'), color: 'bg-lime-500' },
    { value: 5, label: t('assessment.scale.5', 'Strongly Agree'), color: 'bg-green-500' }
  ];

  const handleAnswer = (value) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion].id]: value
    }));
  };

  const goToNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const goToPrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const calculateResults = () => {
    // Calculate scores per dimension
    const dimensionScores = {};
    const dimensionQuestions = {};

    // Group questions by dimension
    questions.forEach(q => {
      if (!dimensionQuestions[q.dimension]) {
        dimensionQuestions[q.dimension] = [];
      }
      dimensionQuestions[q.dimension].push(q.id);
    });

    // Calculate average score per dimension (scale 1-5, convert to 0-100)
    Object.keys(dimensionQuestions).forEach(dim => {
      const questionIds = dimensionQuestions[dim];
      const totalScore = questionIds.reduce((sum, qId) => sum + (answers[qId] || 3), 0);
      const avgScore = totalScore / questionIds.length;
      dimensionScores[dim] = Math.round((avgScore - 1) * 25); // Convert 1-5 to 0-100
    });

    // Calculate overall score
    const overallScore = Math.round(
      Object.values(dimensionScores).reduce((a, b) => a + b, 0) / 5
    );

    // Identify areas needing support (below 50%)
    const areasNeedingSupport = Object.entries(dimensionScores)
      .filter(([_, score]) => score < 50)
      .map(([dim, score]) => ({ dimension: dim, score }))
      .sort((a, b) => a.score - b.score);

    // Identify strengths (above 70%)
    const strengths = Object.entries(dimensionScores)
      .filter(([_, score]) => score >= 70)
      .map(([dim, score]) => ({ dimension: dim, score }))
      .sort((a, b) => b.score - a.score);

    setResults({
      dimensionScores,
      overallScore,
      areasNeedingSupport,
      strengths,
      totalQuestions: questions.length,
      completedAt: new Date().toISOString()
    });

    setShowResults(true);
    setStep('results');

    // Auto-submit results to backend (email PDF + subscribe)
    fetch(apiUrl('assessment/submit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: userInfo.name,
        email: userInfo.email,
        overallScore,
        dimensionScores,
        areasNeedingSupport,
        strengths,
        totalQuestions: questions.length,
        completedAt: new Date().toISOString(),
      }),
    }).catch(error => logger.error('Failed to submit assessment:', error));
  };

  const handlePrint = () => {
    const dimensionsList = Object.entries(dimensions).map(([key, dim]) => {
      const score = results.dimensionScores[key];
      return `
        <div style="display: flex; align-items: center; margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 8px;">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #10b981, #059669); border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 16px;">
            <span style="color: white; font-weight: bold;">${dim.name.charAt(0)}</span>
          </div>
          <div style="flex: 1;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span style="font-weight: 600; color: #374151;">${dim.name}</span>
              <span style="font-weight: bold; color: ${score >= 70 ? '#16a34a' : score >= 50 ? '#ca8a04' : '#dc2626'};">${score}%</span>
            </div>
            <div style="height: 8px; background: #e5e7eb; border-radius: 4px; overflow: hidden;">
              <div style="height: 100%; width: ${score}%; background: linear-gradient(90deg, #10b981, #059669); border-radius: 4px;"></div>
            </div>
            <p style="font-size: 12px; color: #6b7280; margin-top: 4px;">${dim.description}</p>
          </div>
        </div>
      `;
    }).join('');

    const priorityAreas = results.areasNeedingSupport.map(({ dimension, score }) => {
      const dim = dimensions[dimension];
      return `
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px; background: white; border-radius: 8px; margin-bottom: 8px;">
          <div>
            <p style="font-weight: 600; color: #1f2937;">${dim.name}</p>
            <p style="font-size: 12px; color: #6b7280;">${dim.description}</p>
          </div>
          <span style="font-weight: bold; color: #dc2626;">${score}%</span>
        </div>
      `;
    }).join('');

    const strengthsList = results.strengths.map(({ dimension, score }) => {
      const dim = dimensions[dimension];
      return `<span style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: white; border: 1px solid #86efac; border-radius: 20px; margin: 4px;">${dim.name} <strong style="color: #16a34a;">${score}%</strong></span>`;
    }).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>CoachMeUpp - 5D Assessment Report</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 40px; color: #1f2937; }
          .header { background: linear-gradient(135deg, #10b981, #0d9488); color: white; padding: 24px; border-radius: 12px; margin-bottom: 32px; }
          .header h1 { margin: 0 0 8px 0; font-size: 24px; }
          .header p { margin: 0; opacity: 0.9; }
          .score-circle { width: 120px; height: 120px; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 0 auto 24px; }
          .section { margin-bottom: 32px; }
          .section-title { font-size: 18px; font-weight: 600; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
          .priority-box { background: #fef2f2; border: 1px solid #fecaca; padding: 20px; border-radius: 12px; }
          .strengths-box { background: #f0fdf4; border: 1px solid #bbf7d0; padding: 20px; border-radius: 12px; }
          .recommendations-box { background: #eff6ff; border: 1px solid #bfdbfe; padding: 20px; border-radius: 12px; }
          .recommendations-box ul { margin: 0; padding-left: 20px; }
          .recommendations-box li { margin-bottom: 8px; color: #1e40af; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🎯 Your 5D Assessment Results</h1>
          <p>Personalized Coaching Insights Report</p>
        </div>

        <div style="text-align: center; margin-bottom: 32px;">
          <p style="color: #6b7280; margin-bottom: 8px;">Overall Readiness Score</p>
          <div class="score-circle" style="background: ${results.overallScore >= 70 ? '#dcfce7' : results.overallScore >= 50 ? '#fef9c3' : '#fee2e2'};">
            <span style="font-size: 36px; font-weight: bold; color: ${results.overallScore >= 70 ? '#16a34a' : results.overallScore >= 50 ? '#ca8a04' : '#dc2626'};">${results.overallScore}%</span>
            <span style="font-size: 14px; color: ${results.overallScore >= 70 ? '#16a34a' : results.overallScore >= 50 ? '#ca8a04' : '#dc2626'};">${getScoreLabel(results.overallScore)}</span>
          </div>
        </div>

        <div class="section">
          <h2 class="section-title">📊 5D Dimension Scores</h2>
          ${dimensionsList}
        </div>

        ${results.areasNeedingSupport.length > 0 ? `
        <div class="section">
          <div class="priority-box">
            <h2 class="section-title" style="color: #991b1b;">⚠️ Priority Areas for Coaching</h2>
            <p style="color: #b91c1c; margin-bottom: 16px;">These areas could benefit most from focused coaching support:</p>
            ${priorityAreas}
          </div>
        </div>
        ` : ''}

        ${results.strengths.length > 0 ? `
        <div class="section">
          <div class="strengths-box">
            <h2 class="section-title" style="color: #166534;">✅ Your Strengths</h2>
            <p style="color: #15803d; margin-bottom: 16px;">These areas show strong foundations to build upon:</p>
            <div>${strengthsList}</div>
          </div>
        </div>
        ` : ''}

        <div class="section">
          <div class="recommendations-box">
            <h2 class="section-title" style="color: #1e40af;">💡 Personalized Recommendations</h2>
            <ul>
              ${results.areasNeedingSupport.length > 0 ? `
              <li>Focus your coaching sessions on your priority areas for maximum impact.</li>
              <li>Set specific, measurable goals for each priority dimension.</li>
              <li>Leverage your strengths to support growth in weaker areas.</li>
              ` : `
              <li>Great foundation! Consider advanced coaching to reach excellence.</li>
              <li>Focus on maintaining consistency and preventing burnout.</li>
              `}
              <li>Retake this assessment every 3 months to track your progress.</li>
            </ul>
          </div>
        </div>

        <div class="footer">
          <p><strong>CoachMeUpp</strong> - 5D Assessment Report</p>
          <p>Generated on ${new Date().toLocaleDateString()} | www.coachmeupp.com</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([printContent], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    printFrame.src = blobUrl;
    document.body.appendChild(printFrame);
    
    printFrame.onload = () => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(printFrame);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    };
  };

  const resetAssessment = () => {
    setStep('info');
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResults(null);
    setUserInfo({ name: '', email: '' });
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return t('assessment.scoreLabels.excellent', 'Excellent');
    if (score >= 70) return t('assessment.scoreLabels.good', 'Good');
    if (score >= 50) return t('assessment.scoreLabels.developing', 'Developing');
    if (score >= 30) return t('assessment.scoreLabels.needsWork', 'Needs Work');
    return t('assessment.scoreLabels.critical', 'Priority Area');
  };

  if (!isOpen) return null;

  // Results View
  if (showResults && results) {
    return (
      <AssessmentResults
        results={results}
        dimensions={dimensions}
        onClose={onClose}
        onComplete={onComplete}
        onPrint={handlePrint}
        onReset={resetAssessment}
        reportRef={reportRef}
        getScoreColor={getScoreColor}
        getScoreBg={getScoreBg}
        getScoreLabel={getScoreLabel}
        userName={userInfo.name}
        userEmail={userInfo.email}
      />
    );
  }

  // Info Collection Step
  if (step === 'info') {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="assessment-info-modal">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{t('assessment.title', '5D Self-Assessment')}</h2>
                  <p className="text-emerald-100 text-sm">{t('assessment.infoSubtitle', 'Discover your coaching readiness in 5 minutes')}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Info Form */}
          <div className="p-6">
            <p className="text-gray-600 mb-6">
              {t('assessment.infoDesc', "Enter your details to get started. Your personalized results will be emailed to you as a report upon completion.")}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  {t('assessment.infoName', 'Your Name')} *
                </label>
                <Input
                  value={userInfo.name}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  required
                  data-testid="assessment-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  {t('assessment.infoEmail', 'Your Email')} *
                </label>
                <Input
                  type="email"
                  value={userInfo.email}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  required
                  data-testid="assessment-email-input"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {t('assessment.infoEmailNote', "We'll send your detailed results report to this email.")}
                </p>
              </div>
            </div>

            <Button
              onClick={() => setStep('questions')}
              disabled={!userInfo.name.trim() || !userInfo.email.trim() || !userInfo.email.includes('@')}
              className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 py-5 text-base font-semibold"
              data-testid="assessment-start-btn"
            >
              {t('assessment.startAssessment', 'Start Assessment')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Question View
  const question = questions[currentQuestion];
  const dimension = dimensions[question.dimension];
  const DimIcon = dimension.icon;
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" data-testid="assessment-modal">
        {/* Header */}
        <div className={`bg-gradient-to-r ${dimension.gradient} px-6 py-5 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <DimIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('assessment.title', '5D Self-Assessment')}</h2>
                <p className="text-white/80 text-sm">{dimension.name} - {t('assessment.questionOf', 'Question')} {currentQuestion + 1} / {questions.length}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-testid="assessment-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-2 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Question */}
          <div className="mb-8">
            <p className="text-xl font-semibold text-gray-900 mb-2">{question.text}</p>
            <p className="text-sm text-gray-500">{question.subtext}</p>
          </div>

          {/* Answer Options */}
          <div className="space-y-3 mb-8">
            {answerOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  answers[question.id] === option.value
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`answer-option-${option.value}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  answers[question.id] === option.value ? 'bg-emerald-500' : option.color
                } text-white font-bold text-sm`}>
                  {option.value}
                </div>
                <span className={`font-medium ${
                  answers[question.id] === option.value ? 'text-emerald-700' : 'text-gray-700'
                }`}>
                  {option.label}
                </span>
                {answers[question.id] === option.value && (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 ml-auto" />
                )}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={goToPrevious}
              disabled={currentQuestion === 0}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('assessment.previous', 'Previous')}
            </Button>

            <div className="flex items-center gap-1">
              {questions.map((q, idx) => (
                <div
                  key={q.id}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentQuestion 
                      ? 'bg-emerald-500' 
                      : answers[questions[idx].id] 
                        ? 'bg-emerald-300' 
                        : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={goToNext}
              disabled={!answers[question.id]}
              className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            >
              {currentQuestion === questions.length - 1 
                ? t('assessment.seeResults', 'See Results') 
                : t('assessment.next', 'Next')
              }
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiveDAssessment;
